import { useContext, ref, reactive, useRoute } from "@nuxtjs/composition-api";
import useVuelidate from "@vuelidate/core";
import { useLogin } from "../auth";
import errorCodes from "~/helpers/errorCodes";

const initialFormData = (email) => ({
  email: email || "",
  password: "",
});

/**
 * @param {Object} [options]
 * @param {boolean} [options.navigate=true] - Navigate to the next page on
 *   success. Set false when logging in from a dialog to stay on the page.
 * @param {Function} [options.onSuccess] - Called after a successful login.
 */
export function useLoginForm({ navigate = true, onSuccess } = {}) {
  const {
    $validation: { rules },
    $logger,
    i18n,
  } = useContext();
  const submitting = ref(false);
  const requestError = ref(null);
  const login = useLogin();

  // $mutate rejects with the already-translated error message (the raw code is
  // lost), so NO_PASSWORD is recognized either as the untranslated code or as
  // its translation in the current locale.
  function isNoPasswordError(e) {
    return (
      e === "NO_PASSWORD" ||
      (i18n.te("error.messages.NO_PASSWORD") &&
        e === i18n.t("error.messages.NO_PASSWORD"))
    );
  }
  const route = useRoute();
  // Pages written from legacy PHP can redirect to login page with an email
  // We'll use it as an initial value for the form
  const email = route.value.query.email;
  const formData = reactive(initialFormData(email));

  // The specify-tender flow redirects here with noPassword=true when it
  // already knows from tenderByKey that the account has no password. Password
  // login can never succeed then, so offer the alternatives right away. No
  // set-password email exists yet on this path (the backend only sends one
  // after a failed login attempt), hence the requestRecovery variant.
  const noPasswordModalOpen = ref(route.value.query.noPassword === "true");
  const noPasswordModalVariant = ref(
    noPasswordModalOpen.value ? "requestRecovery" : "emailSent"
  );

  function resetForm() {
    Object.assign(formData, initialFormData());
  }

  const validationArgs = {
    email: {
      required: rules.required,
      email: rules.email,
      maxLength: rules.maxLength(100),
    },
    password: {
      required: rules.required,
      maxLength: rules.maxLength(50),
    },
  };
  const v = useVuelidate(validationArgs, formData, {
    $lazy: true,
  });

  async function submitLoginForm() {
    try {
      submitting.value = true;
      requestError.value = null;

      const isValid = await v.value.$validate();
      if (!isValid) return;

      await login(
        {
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
        },
        { navigate }
      );

      if (onSuccess) {
        await onSuccess();
      }
    } catch (e) {
      if (typeof e === "string") {
        if (isNoPasswordError(e)) {
          // The rejected attempt made the backend send the set-password email.
          noPasswordModalVariant.value = "emailSent";
          noPasswordModalOpen.value = true;
          return;
        }
        requestError.value = e;
        return;
      }
      requestError.value = i18n.t("error.unknown");

      $logger.error(errorCodes.UNKNOWN_ERROR_DURING_LOGIN, e);
    } finally {
      submitting.value = false;
    }
  }

  return {
    submitting,
    formData,
    resetForm,
    v,
    requestError,
    noPasswordModalOpen,
    noPasswordModalVariant,
    submitLoginForm,
  };
}
